/**
 * donationCampaignService.js
 * Frontend service for donation campaigns — direct NCB, no Express dependency.
 * 
 * Schema Fixes:
 * - campaign_id: Coerced to Number or null (prevent string-to-int error)
 * - amount: Ensured numeric precision
 * - timestamps: Added created_at to donation records
 */

const NCB_DATA_URL    = 'https://app.nocodebackend.com/api/data';
const NCB_INSTANCE    = '53179_memorial_sanctuary';
const NCB_SECRET      = '8538d058b9882f511ddf0d39c7d91bc6027abeeb4b8b69523ca8a6d1fdef';
const CAMPAIGNS_TABLE = 'donation_campaigns_1776500000';
const DONATIONS_TABLE = 'memorial_donations_1776800000';
const MONET_TABLE     = 'monetization_config_1776389352234';

const ncbHeaders = () => ({
  'Content-Type':        'application/json',
  'Authorization':       `Bearer ${NCB_SECRET}`,
  'X-Database-Instance': NCB_INSTANCE,
});

function ncbUrl(path, extra = '') {
  const sep = path.includes('?') ? '&' : '?';
  return `${NCB_DATA_URL}/${path}${sep}Instance=${NCB_INSTANCE}${extra ? '&' + extra : ''}`;
}

function parseCampaign(row) {
  if (!row) return row;
  let details = row.beneficiary_details;
  if (typeof details === 'string') {
    try { details = JSON.parse(details); } catch { details = {}; }
  }
  return { ...row, beneficiary_details: details };
}

// ── BENEFICIARY TYPES ────────────────────────────────────────────
export const BENEFICIARY_TYPES = [
  {
    id: 'charity', label: 'Registered Charity', emoji: '🏛️',
    description: 'A registered non-profit or charitable organisation.',
    fields: [
      { key: 'charity_name',         label: 'Charity Name',          required: true  },
      { key: 'charity_registration', label: 'Registration Number',   required: true  },
      { key: 'website',              label: 'Website (optional)',     required: false },
    ],
  },
  {
    id: 'individual', label: 'Individual / Family Member', emoji: '👤',
    description: 'A named individual (e.g. surviving family member).',
    fields: [
      { key: 'full_name',     label: 'Full Name',                required: true  },
      { key: 'relationship',  label: 'Relationship to Deceased', required: true  },
      { key: 'contact_email', label: 'Contact Email (optional)', required: false },
    ],
  },
  {
    id: 'family', label: 'Family Group', emoji: '👨‍👩‍👧',
    description: 'Funds directed to the family collectively.',
    fields: [
      { key: 'family_name',   label: 'Family Name',               required: true  },
      { key: 'contact_name',  label: 'Contact Person (optional)', required: false },
      { key: 'contact_email', label: 'Contact Email (optional)',  required: false },
    ],
  },
  {
    id: 'memorial_fund', label: 'Memorial Fund', emoji: '🕊️',
    description: 'A dedicated fund in memory of the deceased.',
    fields: [
      { key: 'description', label: 'Fund Description (optional)', required: false },
    ],
  },
];

export function getBeneficiaryType(id) {
  return BENEFICIARY_TYPES.find(t => t.id === id) || null;
}

// ── DONATION CONFIG (fee + tips) ──────────────────────────────────
export async function fetchDonationConfig() {
  try {
    const res  = await fetch(ncbUrl(`read/${MONET_TABLE}`, 'category=donations&limit=20'), { headers: ncbHeaders() });
    const data = await res.json();
    const rows = Array.isArray(data.data) ? data.data : [];
    const get  = (key, def) => {
      const r = rows.find(r => r.config_key === key);
      return r ? r.config_value : def;
    };
    return {
      feePct:     parseFloat(get('donation_platform_fee_pct', '10')),
      feeRate:    parseFloat(get('donation_platform_fee_pct', '10')) / 100,
      tipOptions: [
        parseInt(get('donation_tip_option_1_pct', '1')),
        parseInt(get('donation_tip_option_2_pct', '5')),
        parseInt(get('donation_tip_option_3_pct', '10')),
      ],
      allowTip:   get('donation_allow_donor_tip', 'true') === 'true',
      maxTipPct:  parseInt(get('donation_max_tip_pct', '10')),
      minAmount:  parseInt(get('donation_min_amount_usd', '1')),
    };
  } catch {
    return { feePct: 10, feeRate: 0.10, tipOptions: [1, 5, 10], allowTip: true, maxTipPct: 10, minAmount: 1 };
  }
}

export async function fetchPlatformFeePct() {
  const cfg = await fetchDonationConfig();
  return cfg.feePct;
}

// ── CAMPAIGN CRUD ─────────────────────────────────────────────────
export async function createCampaign(payload) {
  const cfg = await fetchDonationConfig();
  const now = new Date().toISOString();
  
  const body = {
    owner_id:            payload.owner_id,
    title:               payload.title,
    description:         payload.description,
    goal_amount:         Number(payload.goal_amount),
    raised_amount:       0,
    deadline:            payload.deadline,
    memorial_id:         payload.memorial_id   || '',
    memorial_name:       payload.memorial_name || '',
    beneficiary_type:    payload.beneficiary_type,
    beneficiary_name:    payload.beneficiary_name,
    beneficiary_details: JSON.stringify(payload.beneficiary_details || {}),
    cover_image_url:     payload.cover_image_url || '',
    status:              'active',
    donor_count:         0,
    is_featured:         0,
    platform_fee_pct:    cfg.feePct,
    created_at:          now,
    updated_at:          now,
  };

  const res  = await fetch(ncbUrl(`create/${CAMPAIGNS_TABLE}`), {
    method: 'POST', headers: ncbHeaders(), body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) {
    const msg = data?.message || data?.error || 'Failed to create campaign.';
    throw new Error(msg);
  }
  return { success: true, campaignId: data.id || data.data?.id, data: { ...body, id: data.id || data.data?.id } };
}

export async function listCampaigns(filters = {}) {
  let extra = '';
  if (filters.status)      extra += `&status=${filters.status}`;
  if (filters.memorial_id) extra += `&memorial_id=${encodeURIComponent(filters.memorial_id)}`;
  if (filters.page)        extra += `&page=${filters.page}`;
  if (filters.limit)       extra += `&limit=${filters.limit}`;

  const res  = await fetch(ncbUrl(`read/${CAMPAIGNS_TABLE}`, extra.slice(1)), { headers: ncbHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error('Failed to fetch campaigns.');
  const campaigns = (Array.isArray(data.data) ? data.data : []).map(parseCampaign);
  return { success: true, campaigns, count: campaigns.length, metadata: data.metadata || {} };
}

export async function getCampaign(id) {
  const res  = await fetch(ncbUrl(`read/${CAMPAIGNS_TABLE}/${id}`), { headers: ncbHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error('Campaign not found.');
  return { success: true, campaign: parseCampaign(data.data) };
}

export async function updateCampaign(id, updates) {
  const res  = await fetch(ncbUrl(`update/${CAMPAIGNS_TABLE}/${id}`), {
    method: 'PUT', headers: ncbHeaders(), body: JSON.stringify({ ...updates, updated_at: new Date().toISOString() }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.message || 'Failed to update campaign.');
  return { success: true };
}

// ── DONATION SUBMISSION ───────────────────────────────────────────
export async function submitDonation(donationData) {
  const transactionRef = `TXN-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  
  // ★ Schema alignment fix: ensure campaign_id is integer or null
  const campId = donationData.campaign_id ? parseInt(donationData.campaign_id, 10) : null;
  
  const payload = {
    owner_id:           'guest',
    memorial_id:        donationData.memorial_id    || '',
    memorial_name:      donationData.memorial_name  || '',
    donor_name:         donationData.is_anonymous ? 'Anonymous' : (donationData.donor_name || 'Anonymous'),
    donor_email:        donationData.donor_email    || '',
    amount:             Number(donationData.amount) || 0,
    donor_tip:          Number(donationData.donor_tip) || 0,
    platform_fee_pct:   Number(donationData.platform_fee_pct) || 10,
    gift_type:          donationData.gift_type      || '',
    gift_option:        donationData.gift_option    || '',
    message:            donationData.message        || '',
    donation_type:      donationData.donation_type  || 'one_time',
    recurring_interval: donationData.donation_type === 'recurring' ? (donationData.recurring_interval || 'monthly') : '',
    is_anonymous:       donationData.is_anonymous   ? 1 : 0,
    charity_name:       donationData.charity_name   || 'Memorial Fund',
    campaign_id:        campId, // integer or null
    transaction_ref:    transactionRef,
    status:             'completed',
    created_at:         new Date().toISOString(),
  };

  const res  = await fetch(ncbUrl(`create/${DONATIONS_TABLE}`), {
    method: 'POST', headers: ncbHeaders(), body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.message || data?.error || 'Failed to record donation.');

  if (campId) {
    try {
      const campRes  = await fetch(ncbUrl(`read/${CAMPAIGNS_TABLE}/${campId}`), { headers: ncbHeaders() });
      const campData = await campRes.json();
      if (campRes.ok && campData.data) {
        await fetch(ncbUrl(`update/${CAMPAIGNS_TABLE}/${campId}`), {
          method: 'PUT', headers: ncbHeaders(),
          body: JSON.stringify({
            raised_amount: (Number(campData.data.raised_amount) || 0) + Number(donationData.amount),
            donor_count:   (Number(campData.data.donor_count)   || 0) + 1,
            updated_at:    new Date().toISOString(),
          }),
        });
      }
    } catch { /* silent */ }
  }

  return { success: true, donationId: data.id || data.data?.id, transactionRef, data: payload };
}

export async function getDonationsByMemorial(memorialId, opts = {}) {
  const { page = 1, limit = 50 } = opts;
  const extra = `memorial_id=${encodeURIComponent(memorialId)}&page=${page}&limit=${limit}&sort=created_at&order=desc`;
  const res   = await fetch(ncbUrl(`read/${DONATIONS_TABLE}`, extra), { headers: ncbHeaders() });
  const data  = await res.json();
  if (!res.ok) throw new Error('Failed to fetch donations.');
  const donations = Array.isArray(data.data) ? data.data : [];
  return { success: true, donations, count: donations.length, totalAmount: donations.reduce((s, d) => s + (Number(d.amount) || 0), 0), metadata: data.metadata || {} };
}